import React, { useMemo, useState } from "react";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import DashboardAdherentsHeader from "./components/DashboardAdherentsHeader";
import StatisticsCards from "./components/StatisticsCards";
import DashboardCollapsibleSection from "./components/DashboardCollapsibleSection";
import VisitesLineChart from "./components/VisitesLineChart";
import ComingSoonPanel from "./components/ComingSoonPanel";
import AdherentStatusBreakdownCard from "./components/AdherentStatusBreakdownCard";
import GroupDistributionChart from "./components/GroupDistributionChart";
import LatestMembers from "./components/LatestMembers";
import RenewedMembers from "./components/RenewedMembers";
import CommuneDistributionChart from "./components/CommuneDistributionChart";
import CommunityStatisticsTable from "./components/CommunityStatisticsTable";
import GenderAgeDistributionChart from "./components/GenderAgeDistributionChart";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaChartLine,
  FaUsersCog,
  FaVenusMars,
  FaTable,
  FaMapMarkedAlt,
  FaChalkboardTeacher,
  FaUserClock,
  FaClipboardCheck,
} from "react-icons/fa";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Dashboard administrateur
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function DashboardAdherents() {
  useScrollToTop();

  const { user } = useUser();

  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const [openSections, setOpenSections] = useState({
    visites: true,
    statuts: true,
    cours: false,
    genreAge: false,
    statsDetaillees: false,
    communes: false,
    statsCours: false,
    derniersInscrits: true,
  });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardAdherentsHeader />

        <StatisticsCards />

        <DashboardCollapsibleSection
          eyebrow="Inscriptions"
          title="Derniers adhérents inscrits"
          description="Liste compacte des dernières fiches adhérents créées dans le club."
          icon={<FaUserClock />}
          isOpen={openSections.derniersInscrits}
          onToggle={() => toggleSection("derniersInscrits")}
        >
          <LatestMembers />
        </DashboardCollapsibleSection>

        <DashboardCollapsibleSection
          eyebrow="Renouvellements"
          title="Derniers adhérents renouvelés"
          description="Liste compacte des dernières fiches adhérents renouvelées dans le club."
          icon={<FaChalkboardTeacher />}
          isOpen={openSections.renouvellements}
          onToggle={() => toggleSection("renouvellements")}
        >
          <RenewedMembers />
        </DashboardCollapsibleSection>

        <DashboardCollapsibleSection
          eyebrow="Visites"
          title="Évolution des visites"
          description="Suivez les visites par jour, filtrez par période, activez le lissage et nettoyez les anciennes données si nécessaire."
          icon={<FaChartLine />}
          isOpen={openSections.visites}
          onToggle={() => toggleSection("visites")}
        >
          <VisitesLineChart />
        </DashboardCollapsibleSection>

        <DashboardCollapsibleSection
          eyebrow="Dossiers"
          title="Répartition des statuts d'inscription"
          description="Analyse des adhérents selon leur statut d’inscription pour la saison active."
          icon={<FaClipboardCheck />}
          isOpen={openSections.statuts}
          onToggle={() => toggleSection("statuts")}
        >
          <AdherentStatusBreakdownCard />
        </DashboardCollapsibleSection>

        <DashboardCollapsibleSection
          eyebrow="Répartition"
          title="Répartition des adhérents par cours"
          description="Graphique des adhérents répartis par groupes, cours ou créneaux."
          icon={<FaUsersCog />}
          isOpen={openSections.cours}
          onToggle={() => toggleSection("cours")}
        >
          <GroupDistributionChart />
        </DashboardCollapsibleSection>

        <DashboardCollapsibleSection
          eyebrow="Géographie"
          title="Répartition par commune"
          description="Visualisation des communes représentées par les adhérents de la saison active."
          icon={<FaMapMarkedAlt />}
          isOpen={openSections.communes}
          onToggle={() => toggleSection("communes")}
        >
          <CommuneDistributionChart />
        </DashboardCollapsibleSection>

        <DashboardCollapsibleSection
          eyebrow="Tableau"
          title="Statistiques communes, sexe et âge"
          description="Tableau prêt à copier pour vos dossiers de subvention."
          icon={<FaTable />}
          isOpen={openSections.statsDetaillees}
          onToggle={() => toggleSection("statsDetaillees")}
        >
          <CommunityStatisticsTable />
        </DashboardCollapsibleSection>

        <DashboardCollapsibleSection
          eyebrow="Profil adhérents"
          title="Répartition genre & âge"
          description="Analyse globale des adhérents par genre, mineurs et majeurs."
          icon={<FaVenusMars />}
          isOpen={openSections.genreAge}
          onToggle={() => toggleSection("genreAge")}
        >
          <GenderAgeDistributionChart />
        </DashboardCollapsibleSection>
      </div>
      <ToastContainer />
    </section>
  );
}
